import { Button, Select, useToast } from '@chakra-ui/react';
import AdminPageShell from '../../../components/layout/AdminPageShell';
import ServicesTable from '../../../components/Services/ServicesTable';
import { columnsDataServices } from './variables/columnsDataServices.js';
import React, { useEffect, useState } from 'react';
import { getData, postData, putData } from '../../../utils/httpClient.js';
import { useSearch } from '../../../contexts/SearchContext.js';
import ServicesEditModal from '../../../components/Services/ServicesEditModal.js';
import { useResetPageOnFilterChange } from '../../../hooks/useResetPageOnFilterChange.js';
import { useTableSorting } from '../../../hooks/useTableSorting.js';
import { useExtraTableFilter } from '../../../hooks/useExtraTableFilter.js';

export default function Settings() {
  const { searchQuery, resetListFilters } = useSearch();
  const {
    draft: modelFilter,
    setDraft: setModelFilter,
    applied: appliedModelFilter,
  } = useExtraTableFilter('');
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [modalState, setModalState] = useState({
    isOpen: false,
    service: null,
    isNew: false,
  });
  const [models, setModels] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const toast = useToast();
  const { sorting, setSorting, sortBy, sortDir, appendSortParams } = useTableSorting();

  useResetPageOnFilterChange(setPage, page, [searchQuery, appliedModelFilter, rowsPerPage, sortBy, sortDir]);

  const loadModels = async () => {
    try {
      const response = await getData('/models', { skipLoader: true });
      const list = response?.payload?.models || [];
      setModels(
        [...list].sort((a, b) =>
          String(a.description || '').localeCompare(
            String(b.description || ''),
            'es',
            { numeric: true },
          ),
        ),
      );
    } catch {
      setModels([]);
    }
  };

  const loadData = async (dato, modelId, pageOverride) => {
    try {
      const activePage = pageOverride ?? page;
      const params = new URLSearchParams({
        page: String(activePage + 1),
        limit: String(rowsPerPage),
        data: dato || '',
      });
      if (modelId) params.set('modelId', String(modelId));
      appendSortParams(params);
      const response = await getData(`/services?${params.toString()}`);
      setData(response?.payload?.services || []);
      setTotalPages(Math.ceil((response?.payload?.total || 0) / rowsPerPage));
    } catch (error) {
      toast({
        title: 'Error al obtener servicios',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    loadModels();
  }, []);

  useEffect(() => {
    loadData(searchQuery, appliedModelFilter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchQuery, appliedModelFilter, rowsPerPage, sortBy, sortDir]);

  useEffect(() => {
    resetListFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNewServiceModal = () => {
    setModalState({
      isOpen: true,
      service: {
        id: null,
        name: '',
        serviceTypeId: 1,
        enabled: true,
      },
      isNew: true,
    });
  };

  const closeEditModal = () => {
    setModalState({ isOpen: false, service: null, isNew: false });
  };

  const handleCreateService = async (newService) => {
    try {
      await postData('/services', newService);
      setPage(0);
      await loadData(searchQuery, appliedModelFilter, 0);
      closeEditModal();
    } catch (error) {
      toast({
        title: 'Error al crear servicio',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleUpdateService = async (service) => {
    try {
      await putData(`/services/${service.id}`, service);
      setPage(0);
      await loadData(searchQuery, appliedModelFilter, 0);
      closeEditModal();
    } catch (error) {
      toast({
        title: 'Error al actualizar el servicio',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDuplicateService = async (service) => {
    try {
      const duplicatedService = {
        ...service,
        id: null,
        serviceTypeId: 1,
        enabled: true,
      };
      await handleCreateService(duplicatedService);
      toast({
        title: 'Servicio duplicado correctamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error al duplicar el servicio',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <AdminPageShell
      title="Servicios"
      actions={
        <>
          <Select
            size="sm"
            maxW="240px"
            bg="white"
            value={modelFilter}
            onChange={(e) => {
              setModelFilter(e.target.value);
            }}
            placeholder="Todos los modelos"
          >
            {models.map((model) => (
              <option key={model.id} value={model.id}>
                {model.description}
              </option>
            ))}
          </Select>
          <Button
            variant="adminPrimary"
            size="sm"
            flexShrink={0}
            whiteSpace="nowrap"
            onClick={openNewServiceModal}
          >
            Nuevo servicio
          </Button>
        </>
      }
    >
      <ServicesTable
        columnsData={columnsDataServices}
        data={data}
        page={page}
        setPage={setPage}
        totalPages={totalPages}
          pageSize={rowsPerPage}
          setPageSize={setRowsPerPage}
        handleUpdateService={handleUpdateService}
        handleDuplicateService={handleDuplicateService}
        sorting={sorting}
        onSortingChange={setSorting}
      />

      <ServicesEditModal
        isOpen={modalState.isOpen}
        onClose={closeEditModal}
        service={modalState.service}
        isNew={modalState.isNew}
        onCreate={handleCreateService}
      />
    </AdminPageShell>
  );
}
